import type { ChangeEvent, MouseEvent } from 'react';
import TooltipLabel from '@/components/TooltipLabel';
import { LoadingIndicator } from '@/components/loading-indicator';
import { useOrgContext } from '@/contexts/orgContext';
import { MetaAddonsType, useAddonsMeta } from '@/hooks/useAddonsMeta';
import { EnabledAddonsType, useListEnabledAddons } from '@/hooks/useListEnabledAddons';
import { isOrgAdmin } from '@/pages/admin/user/type';
import EditWorkspaceAddons from '@/pages/workgroup/form/addons/edit-drawer';
import { drawerConfigComponentMap } from '@/pages/workgroup/form/addons/edit-drawer/config';
import { WorkspaceT } from '@/pages/workgroup/type';
import { Checkbox } from '@tigergraph/app-ui-lib/checkbox';
import { FC, useCallback, useMemo, useState } from 'react';
import SettingIcon from '@/assets/setting.svg?react';
import IconButton from '@/components/IconButton';
import { Controller, UseFormReturn } from 'react-hook-form';
import { SubFormData } from '@/pages/workgroup/form/type';
import { AddonsMetaID } from '@/components/addons/consts';

const canAddonsConfigure = (addonsID: string) => {
  return Boolean(drawerConfigComponentMap.get(addonsID));
};

const isAddonsEnabled = (addonsMeta: MetaAddonsType, workspace: WorkspaceT) => {
  if (addonsMeta.ID === AddonsMetaID.COPILOT && !workspace.is_rw) {
    return false;
  }
  return true;
};

const WorkspaceAddonsConfiguration: FC<{
  form: UseFormReturn<SubFormData>;
  workspace: WorkspaceT;
}> = ({ form, workspace }) => {
  const { control } = form;
  const { data: addonsMeta, isLoading: isLoadingAddonsMeta } = useAddonsMeta();
  const { data: enabledAddonsMap, isLoading: isLoadingEnabledAddons } = useListEnabledAddons({
    select: useCallback((data?: EnabledAddonsType[]) => new Map(data?.map((item) => [item.AddonsID, item])), []),
  });
  const workspaceAddons = useMemo(() => {
    return addonsMeta?.AddonsList.filter((m) => enabledAddonsMap?.has(m.ID)).filter((m) =>
      isAddonsEnabled(m, workspace)
    );
  }, [addonsMeta, enabledAddonsMap, workspace]);
  const [isAddonsConfigOpen, setIsAddonsConfigOpen] = useState(false);
  const [selectedAddonsMeta, setSelectedAddonsMeta] = useState<MetaAddonsType>();
  const [selectedEnabledAddons, setSelectedEnabledAddons] = useState<EnabledAddonsType>();
  const { userInfo } = useOrgContext();

  const isLoading = isLoadingAddonsMeta || isLoadingEnabledAddons;

  const handleEnableAddons = (addonsMeta: MetaAddonsType) => {
    const enable = form.getValues(`addons.${addonsMeta.ID}.enable`);
    setSelectedAddonsMeta(addonsMeta);
    setSelectedEnabledAddons(enabledAddonsMap?.get(addonsMeta.ID));

    setIsAddonsConfigOpen(enable);
  };

  const handleConfigureAddons = (addonsMeta: MetaAddonsType) => {
    if (addonsMeta.ID === selectedAddonsMeta?.ID) {
      setIsAddonsConfigOpen(!isAddonsConfigOpen);
    } else {
      setIsAddonsConfigOpen(true);
    }
    setSelectedAddonsMeta(addonsMeta);
    setSelectedEnabledAddons(enabledAddonsMap?.get(addonsMeta.ID));
  };

  return (
    <>
      <div className="space-y-2">
        <TooltipLabel label="Add-ons" tooltip="" />
        {isLoading && <LoadingIndicator />}
        {workspaceAddons?.map((m) => (
          <div className="flex items-center rounded-[2px] border border-[#D4DADF] p-3" key={m.ID}>
            <img className="h-8 mr-3" src={m.WorkspaceAvatar} />
            <h4 className="flex-grow text-sm leading-4 font-bold text-[#222]">{m.Title}</h4>
            <span className="text-xs font-bold text-[#294560]">{m.Amount}</span>
            {canAddonsConfigure(m.ID) && (
              <IconButton
                className="ml-1"
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  e.preventDefault();
                  handleConfigureAddons(m);
                }}
              >
                <SettingIcon />
              </IconButton>
            )}
            <Controller
              key={m.ID}
              name={`addons.${m.ID}.enable`}
              control={control}
              render={({ field }) => (
                <Checkbox
                  checkmarkType="toggle"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    field.onChange(e.target.checked);
                    handleEnableAddons(m);
                  }}
                  checked={field.value}
                />
              )}
            />
          </div>
        ))}
        <p className="text-xs font-[Roboto] space-x-1">
          {isOrgAdmin(userInfo.roles) ? (
            <>
              <span className="text-[#656565]">Can{"'"}t find the Add-on you want?</span>
              <a className="text-[#1976D2]" href="/marketplace/addons" target="_blank">
                Click to add
              </a>
              .
            </>
          ) : (
            <span className="text-[#656565]">
              {"Can't find the Add-On you want? Request addition from your organization's administrator."}
            </span>
          )}
        </p>
      </div>
      <EditWorkspaceAddons
        form={form}
        workspace={workspace}
        isOpen={isAddonsConfigOpen}
        selectedAddonsMeta={selectedAddonsMeta}
        selectedEnabledAddons={selectedEnabledAddons}
      />
    </>
  );
};

export default WorkspaceAddonsConfiguration;
